import { forwardRef, ReactNode, MouseEvent, FocusEvent } from "react";
import { motion } from "framer-motion";
import { cn } from "@/lib/utils";
import { springTransition } from "@/lib/animations";
import { useReducedMotion } from "@/hooks/useReducedMotion";

interface ButtonProps {
  variant?: "primary" | "primary-dark" | "secondary" | "accent" | "ghost" | "destructive" | "link";
  size?: "sm" | "md" | "lg";
  isLoading?: boolean;
  fullWidth?: boolean;
  children?: ReactNode;
  className?: string;
  disabled?: boolean;
  type?: "button" | "submit" | "reset";
  onClick?: (e: MouseEvent<HTMLButtonElement>) => void;
  onFocus?: (e: FocusEvent<HTMLButtonElement>) => void;
  onBlur?: (e: FocusEvent<HTMLButtonElement>) => void;
  id?: string;
  name?: string;
  "aria-label"?: string;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      children,
      className,
      variant = "primary",
      size = "md",
      isLoading,
      disabled,
      fullWidth,
      type = "button",
      onClick,
      onFocus,
      onBlur,
      id,
      name,
      "aria-label": ariaLabel,
    },
    ref
  ) => {
    const prefersReducedMotion = useReducedMotion();

    const baseStyles = cn(
      "inline-flex items-center justify-center font-medium",
      "transition-colors duration-150",
      "focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2",
      "disabled:opacity-50 disabled:cursor-not-allowed"
    );

    const variants = {
      primary: cn(
        "bg-[var(--color-primary)] text-white",
        "hover:bg-[var(--color-primary-hover)]",
        "focus-visible:ring-[var(--color-primary)]",
        "active:brightness-90"
      ),
      "primary-dark": cn(
        "bg-[#1F2937] text-white",
        "hover:bg-[#374151]",
        "focus-visible:ring-[#1F2937]",
        "active:bg-[#111827]"
      ),
      secondary: cn(
        "bg-[var(--color-secondary)] text-white",
        "hover:bg-[var(--color-secondary-hover)]",
        "focus-visible:ring-[var(--color-secondary)]",
        "active:brightness-90"
      ),
      accent: cn(
        "bg-[var(--color-accent-custom,var(--color-primary))] text-white",
        "hover:bg-[var(--color-accent-hover,var(--color-primary-hover))]",
        "focus-visible:ring-[var(--color-accent-custom,var(--color-primary))]",
        "active:brightness-90",
        "opacity-100"
      ),
      ghost: cn(
        "bg-[var(--color-bg-hover)] text-[var(--color-text-secondary)]",
        "hover:bg-[var(--color-surface-border-light)]",
        "hover:text-[var(--color-text-primary)]",
        "focus-visible:ring-[var(--color-primary)]"
      ),
      destructive: cn(
        "bg-transparent text-[var(--color-error)]",
        "border border-[var(--color-error)]",
        "hover:bg-[var(--color-error-bg)]",
        "focus-visible:ring-[var(--color-error)]"
      ),
      link: cn(
        "bg-transparent text-[var(--color-primary)]",
        "hover:text-[var(--color-primary-hover)]",
        "underline-offset-2 hover:underline",
        "focus-visible:ring-[var(--color-primary)]",
        "p-0"
      ),
    };

    const sizes = {
      sm: "px-3 py-1.5 min-h-[36px] text-[13px] rounded-md gap-1.5",
      md: "px-4 py-2.5 min-h-[44px] text-[14px] rounded-lg gap-2",
      lg: "px-6 py-3 min-h-[48px] text-[15px] rounded-lg gap-2",
    };

    const isDisabled = disabled || isLoading;

    return (
      <motion.button
        ref={ref}
        type={type}
        id={id}
        name={name}
        aria-label={ariaLabel}
        className={cn(
          baseStyles,
          variants[variant],
          variant !== "link" && sizes[size],
          fullWidth && "w-full",
          className
        )}
        disabled={isDisabled}
        aria-busy={isLoading}
        aria-disabled={isDisabled}
        onClick={onClick}
        onFocus={onFocus}
        onBlur={onBlur}
        whileHover={!prefersReducedMotion && !isDisabled ? { scale: 1.02 } : undefined}
        whileTap={!prefersReducedMotion && !isDisabled ? { scale: 0.98 } : undefined}
        transition={springTransition}
      >
        {isLoading ? (
          <>
            <motion.svg
              className="h-4 w-4"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              aria-hidden="true"
              animate={{ rotate: 360 }}
              transition={{
                duration: 1,
                repeat: Infinity,
                ease: "linear",
              }}
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="3"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </motion.svg>
            <span>Processing...</span>
          </>
        ) : (
          children
        )}
      </motion.button>
    );
  }
);

Button.displayName = "Button";
